// Tiny WebAudio synth — no asset files, works offline. Every sound is generated.
// Call sound.unlock() from a user gesture once; the rest are safe to call anytime.

class Sound {
  private ac: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = false;

  unlock() {
    if (this.ac) {
      if (this.ac.state === "suspended") void this.ac.resume();
      return;
    }
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    this.ac = new Ctx();
    this.master = this.ac.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ac.destination);
  }

  private get t() {
    return this.ac ? this.ac.currentTime : 0;
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType,
    gain = 0.3,
    slideTo?: number,
    delay = 0,
  ) {
    if (!this.ac || !this.master || this.muted) return;
    const t0 = this.t + delay;
    const osc = this.ac.createOscillator();
    const g = this.ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, gain = 0.3, filterHz = 1200, delay = 0) {
    if (!this.ac || !this.master || this.muted) return;
    const t0 = this.t + delay;
    const frames = Math.floor(this.ac.sampleRate * dur);
    const buf = this.ac.createBuffer(1, frames, this.ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / frames);
    const src = this.ac.createBufferSource();
    src.buffer = buf;
    const filt = this.ac.createBiquadFilter();
    filt.type = "lowpass";
    filt.frequency.value = filterHz;
    const g = this.ac.createGain();
    g.gain.value = gain;
    src.connect(filt).connect(g).connect(this.master);
    src.start(t0);
  }

  /** boxing: a whiff as the arm extends */
  swing() {
    this.noise(0.12, 0.12, 900);
  }

  /** boxing: a landed punch — thud + snap */
  hit() {
    this.noise(0.16, 0.45, 500);
    this.tone(140, 0.18, "sine", 0.4, 60);
  }

  /** chess: piece placed */
  place() {
    this.tone(320, 0.06, "triangle", 0.25);
    this.noise(0.04, 0.08, 2500, 0.03);
  }

  /** word duel: a letter typed */
  key() {
    this.tone(660, 0.03, "square", 0.06);
  }

  /** word duel: a guess submitted */
  submit() {
    this.tone(440, 0.08, "triangle", 0.2, 560);
  }

  /** crowd cheer for a win */
  cheer() {
    this.noise(1.4, 0.35, 3500);
    this.noise(1.1, 0.25, 2000, 0.1);
    for (let i = 0; i < 5; i++) {
      this.tone(500 + Math.random() * 400, 0.5, "sawtooth", 0.05, undefined, i * 0.05);
    }
    // rising fanfare
    [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.3, "square", 0.16, undefined, 0.15 + i * 0.12));
  }

  /** soft "aww" for a loss */
  groan() {
    this.tone(300, 0.5, "sawtooth", 0.16, 160);
    this.noise(0.7, 0.14, 1400);
  }

  /** neutral end (draw) */
  whistle() {
    this.tone(880, 0.5, "sine", 0.2, 700);
  }

  /* ---- arcade classics ---- */

  /** asteroids/invaders: your laser */
  laser() {
    this.tone(900, 0.09, "square", 0.12, 220);
  }

  /** asteroids: a rock breaks (bigger rock = deeper boom) */
  rockBoom(size = 2) {
    this.noise(0.18 + size * 0.06, 0.3, 1800 - size * 380);
    this.tone(120 - size * 12, 0.16, "sine", 0.22, 55);
  }

  /** asteroids: a SHIP explodes — distinctly bigger than a rock */
  shipBoom() {
    this.noise(0.55, 0.5, 700);
    this.tone(160, 0.5, "sawtooth", 0.3, 40);
    this.tone(90, 0.6, "sine", 0.35, 30, 0.05);
  }

  /** invaders: one alien down */
  invaderHit() {
    this.noise(0.09, 0.22, 2400);
    this.tone(520, 0.08, "square", 0.14, 160);
  }

  /** invaders: the march tick (pitch cycles for the classic 4-note stomp) */
  march(step: number) {
    this.tone([180, 160, 140, 120][step % 4]!, 0.09, "square", 0.1);
  }

  /** a quick alternating chomp (Bubble Blast: a bubble sticks) */
  chomp(alt: boolean) {
    this.tone(alt ? 520 : 380, 0.05, "square", 0.1, alt ? 380 : 520);
  }

  /** a rising gulp (Bubble Blast: a weapon earned) */
  gulp() {
    [420, 640, 900].forEach((f, i) => this.tone(f, 0.09, "square", 0.16, undefined, i * 0.07));
  }

  /** generic arcade death jingle */
  arcadeDeath() {
    this.tone(600, 0.6, "sawtooth", 0.2, 70);
    this.noise(0.4, 0.2, 900, 0.1);
  }

  /** next level fanfare */
  levelUp() {
    [392, 523, 659, 784].forEach((f, i) => this.tone(f, 0.14, "square", 0.15, undefined, i * 0.09));
  }

  /* ---- tetris ---- */

  /** a piece slams down / locks */
  tetHardDrop() {
    this.tone(150, 0.09, "square", 0.22, 60);
    this.noise(0.06, 0.16, 700);
  }

  /** lines cleared — 1..3 rows a rising blip, 4 (a "tetris") a fanfare */
  tetClear(rows: number) {
    if (rows >= 4) {
      [523, 659, 784, 1047, 1319].forEach((f, i) => this.tone(f, 0.13, "square", 0.16, undefined, i * 0.06));
      this.noise(0.3, 0.16, 3200);
    } else {
      const base = 440 + rows * 120;
      this.tone(base, 0.1, "triangle", 0.2, base * 1.5);
      this.tone(base * 1.5, 0.08, "square", 0.1, undefined, 0.08);
    }
  }

  /** garbage rows shoved into your well */
  tetGarbageIn() {
    this.tone(90, 0.22, "sawtooth", 0.22, 50);
    this.noise(0.18, 0.2, 500);
  }

  /* ---- snake ---- */

  /** snake eats an apple */
  snakeEat() {
    this.tone(560, 0.05, "square", 0.12, 880);
    this.tone(880, 0.06, "square", 0.08, undefined, 0.04);
  }

  /* ---- strait of hormuz ---- */

  /** interceptor launched */
  hzLaunch() {
    this.noise(0.09, 0.28, 2600);
    this.tone(220, 0.16, "sawtooth", 0.22, 780);
    this.tone(90, 0.1, "sine", 0.2, 40);
  }

  /** a threat neutralised — sharp crack + ring */
  hzKill() {
    this.noise(0.13, 0.4, 3200);
    this.tone(880, 0.09, "square", 0.2, 240);
    this.tone(160, 0.18, "sine", 0.28, 60);
  }

  /** a big burst — multiple threats at once */
  hzKillBig() {
    this.noise(0.3, 0.5, 1400);
    this.tone(700, 0.12, "square", 0.22, 120);
    this.tone(120, 0.35, "sawtooth", 0.34, 40);
    [523, 392, 294].forEach((f, i) => this.tone(f, 0.1, "square", 0.14, undefined, 0.05 + i * 0.05));
  }

  /** one of your ships taking a hit */
  hzShipHit() {
    this.noise(0.18, 0.34, 700);
    this.tone(110, 0.22, "sine", 0.3, 48);
  }

  /** a protected ship is lost — alarm + heavy explosion */
  hzShipLost() {
    this.tone(440, 0.5, "sawtooth", 0.24, 180);
    this.tone(200, 0.7, "sine", 0.34, 55, 0.06);
    this.noise(0.6, 0.42, 800, 0.05);
    // two-note klaxon
    this.tone(330, 0.18, "square", 0.16, undefined, 0.0);
    this.tone(247, 0.22, "square", 0.16, undefined, 0.22);
  }

  /** a ship reaches safety */
  hzEscort() {
    this.tone(523, 0.12, "sine", 0.16, undefined, 0);
    this.tone(784, 0.16, "sine", 0.16, undefined, 0.1);
  }

  /* ---- snakes & ladders ---- */

  /** dice rattle + drop */
  snlRoll() {
    for (let i = 0; i < 4; i++) this.noise(0.03, 0.16, 3000, i * 0.045);
    this.tone(180, 0.08, "square", 0.14, 90, 0.2);
  }

  /** climbed a ladder */
  snlLadder() {
    [392, 523, 659, 784, 988].forEach((f, i) => this.tone(f, 0.11, "square", 0.16, undefined, i * 0.07));
  }

  /** slid down a snake */
  snlSnake() {
    this.tone(700, 0.5, "sawtooth", 0.2, 130);
    this.noise(0.35, 0.14, 1600, 0.05);
  }

  /* ---- tug of war ---- */

  tugPull(strength = 1) {
    this.noise(0.08, 0.14 + 0.1 * strength, 700);
    this.tone(140 + 40 * strength, 0.1, "sawtooth", 0.16, 90);
  }
  tugGust() {
    this.noise(0.4, 0.22, 900);
    this.tone(300, 0.4, "sine", 0.1, 140);
  }
  tugWin() {
    [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.12, "square", 0.18, undefined, i * 0.08));
  }
  tugLose() {
    this.tone(300, 0.4, "sawtooth", 0.22, 90);
    this.noise(0.3, 0.2, 500, 0.05);
  }

  /* ---- word battle ---- */

  wordFound(len = 4) {
    const base = 400 + Math.min(len, 8) * 60;
    for (let i = 0; i < Math.min(len, 5); i++) {
      this.tone(base + i * 90, 0.07, "square", 0.14, undefined, i * 0.045);
    }
  }
  wordBig(len = 6) {
    this.wordFound(len);
    this.tone(120, 0.22, "sawtooth", 0.24, 60, 0.05);
    this.noise(0.14, 0.2, 2600, 0.05);
  }
  wordBad() {
    this.tone(200, 0.14, "square", 0.16, 140);
    this.tone(150, 0.16, "square", 0.14, 110, 0.05);
  }
  powerSteal() {
    this.tone(300, 0.14, "sawtooth", 0.2, 900);
    this.tone(900, 0.1, "square", 0.14, undefined, 0.08);
  }
  powerFreeze() {
    [1200, 900, 600, 400].forEach((f, i) => this.tone(f, 0.12, "sine", 0.14, undefined, i * 0.05));
    this.noise(0.3, 0.1, 4000, 0.05);
  }

  /* ---- car soccer ---- */

  carHit(power = 0.5) {
    this.noise(0.07, 0.16 + power * 0.24, 800 + power * 900);
    this.tone(120, 0.1 + power * 0.08, "sine", 0.24, 60);
  }
  carBoost() {
    this.noise(0.5, 0.14, 1600);
    this.tone(160, 0.4, "sawtooth", 0.12, 260);
  }
  carAir() {
    this.tone(500, 0.12, "sine", 0.14, 900);
  }
  carGoal() {
    this.noise(0.35, 0.4, 1400);
    [392, 523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.16, "square", 0.2, undefined, i * 0.06));
    this.tone(90, 0.5, "sawtooth", 0.3, 40, 0.04);
  }
  carWhistle() {
    this.tone(2100, 0.18, "square", 0.16, 2400);
    this.tone(2100, 0.22, "square", 0.16, 2400, 0.22);
  }
}

export const sound = new Sound();
